import type { FunctionComponent } from 'preact';
import { useRef } from 'preact/hooks';

import Table, { type Params as TableParams } from 'Components/smallComponents/table';
import type { TemplateFunction, TemplateFunctionInvoker, TemplateResult } from 'Components/types';
import { useCollapsible } from 'Core/hooks/index';
import { searchResponseExtraSelector } from 'Core/selectors/search';
import { useSelector } from 'Core/store';
import { cloneSafe } from 'Utils/components';
import isMobile from 'Vendor/isMobile';

interface VehicleInfoTableExtra {
  Fields: string[];
  Info: Record<string, string>[];
}

interface Params {
  table: TemplateFunctionInvoker<TableParams>;
  collapsed: boolean;
  toggleCollapsed: () => void;
}

interface Props {
  template: TemplateFunction<Params>;
  initCollapsed?: boolean;
  columnBreakpoint?: number;
}

const VehicleInfo: FunctionComponent<Props> = ({ template, initCollapsed, columnBreakpoint = 768 }) => {
  const rootRef = useRef<HTMLElement>(null);
  const { isCollapsed: collapsed, toggle: toggleCollapsed } = useCollapsible(
    rootRef,
    null,
    initCollapsed ?? isMobile,
  );

  const vehicleInfo = useSelector(searchResponseExtraSelector)?.VehicleInfo as
    | VehicleInfoTableExtra
    | undefined;

  if (!vehicleInfo || !vehicleInfo.Fields || !vehicleInfo.Info) {
    return null;
  }

  const table = (templ: TemplateFunction<TableParams>) => {
    const notEmptyFields = getNotEmptyFields(vehicleInfo.Fields, vehicleInfo.Info);
    const props = { template: templ, fields: notEmptyFields, data: vehicleInfo.Info, columnBreakpoint };
    return (<Table {...props} />) as TemplateResult;
  };

  const component = template.call({ table, collapsed, toggleCollapsed });

  return cloneSafe(component, rootRef);
};

function getNotEmptyFields(fields: string[], data: Record<string, string>[]) {
  const emptyFields = Object.create(null) as Record<string, boolean>;
  let emptyFieldsCount = 0;

  fields.forEach((field) => {
    emptyFields[field] = true;
    emptyFieldsCount += 1;
  });

  data.some((item) => {
    fields.forEach((field) => {
      if (emptyFields[field] && item[field]) {
        delete emptyFields[field];
        emptyFieldsCount -= 1;
      }
    });

    return emptyFieldsCount === 0;
  });

  return fields.filter((field) => !emptyFields[field]);
}

export default VehicleInfo;
